/* Backup (export) and import with a confirmation step. Nothing is written until the person confirms. */
import { backupFileName, createBackup, parseBackup, type ParseResult } from "../domain/backup";
import { dmy, dmyhm, esc } from "../domain/format";
import { totals } from "../domain/library";
import { applyMerge, applyReplace, planImport, type ConflictStrategy } from "../domain/merge";
import { ctx } from "./context";
import { ic } from "./icons";
import { closeSheet, copyText, head, openSheet, toast } from "./shell";
import { FAIL_MESSAGES } from "../data/store";
import { failReasonOf } from "../storage/errors";
import { failNotice, showFail } from "./failure";

export const SAFETY_FAILED_MESSAGE = "หยุดการนำเข้า: เก็บสำเนาข้อมูลเดิมไว้ย้อนกลับไม่ได้ (พื้นที่เก็บข้อมูลเต็มหรือถูกปฏิเสธ) ข้อมูลในเครื่องยังอยู่ครบ ไม่มีอะไรถูกเปลี่ยน";

export let safety: { savedAt: string; reason: string } | null = null;
export function resetSafety() { safety = null; }
const here = () => ctx.mode === "cloud" ? "ในบัญชี" : "ในเครื่อง";
export async function refreshSafety() {
  const store = ctx.store;
  const s = await store.repo.loadSafetyCopy();
  if (store !== ctx.store) return; // the account changed meanwhile: this answer belongs to the previous one
  safety = s ? { savedAt: s.savedAt, reason: s.reason } : null;
}

const backupJson = () => JSON.stringify(createBackup(ctx.store.lib), null, 2);

export function downloadBackup() {
  const blob = new Blob([backupJson()], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = backupFileName(); a.rel = "noopener";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  const t = totals(ctx.store.lib);
  toast(`ส่งออกแล้ว ${t.workspaces} สถานที่ · ${t.categories} หมวด · ${t.actions} Action`);
}

export function openExportText() {
  openSheet(head("คัดลอกไฟล์สำรองเป็นข้อความ")
    + `<p class="note">ใช้เมื่อดาวน์โหลดไฟล์ไม่ได้ คัดลอกข้อความนี้ไปเก็บไว้ แล้ววางในหน้า “นำเข้า” ของอีกเครื่องได้</p>`
    + `<textarea class="inp mono" id="ex-text" readonly rows="8">${esc(backupJson())}</textarea>`
    + `<div class="stack" style="margin-top:12px"><button class="btn primary" id="ex-copy">${ic("copy")}คัดลอก JSON</button></div>`,
  sh => { sh.querySelector<HTMLElement>("#ex-copy")!.onclick = () => copyText(sh.querySelector<HTMLTextAreaElement>("#ex-text")!.value, sh.querySelector("#ex-text")); });
}

export function openImportSheet() {
  openSheet(head("นำเข้าข้อมูล JSON")
    + `<p class="note">เลือกไฟล์สำรองของ MEMOLINK หรือไฟล์ที่ส่งออกจากต้นแบบ ระบบจะตรวจไฟล์และสรุปผลให้ดูก่อน ยังไม่มีอะไรถูกเปลี่ยนจนกว่าจะกดยืนยัน</p>`
    + `<div class="field"><label for="im-file">ไฟล์ .json</label><input class="inp" type="file" id="im-file" accept="application/json,.json,text/plain"></div>`
    + `<div class="field"><label for="im-text">หรือวางข้อความ JSON</label><textarea class="inp mono" id="im-text" rows="5" placeholder='{"format":"memolink-backup", ...}'></textarea></div>`
    + `<button class="btn primary" id="im-check" style="width:100%">${ic("check")}ตรวจข้อมูล</button>`,
  sh => {
    const file = sh.querySelector<HTMLInputElement>("#im-file")!;
    file.addEventListener("change", async () => {
      const f = file.files?.[0]; if (!f) return;
      if (f.size > 5 * 1024 * 1024) return showImportPreview({ ok: false, errors: ["ไฟล์ใหญ่เกิน 5 MB"] }, f.name);
      showImportPreview(parseBackup(await f.text()), f.name);
    });
    sh.querySelector<HTMLElement>("#im-check")!.onclick = () => {
      const text = sh.querySelector<HTMLTextAreaElement>("#im-text")!.value;
      if (!text.trim()) { toast("เลือกไฟล์ หรือวางข้อความ JSON ก่อน"); return; }
      showImportPreview(parseBackup(text), "ข้อความที่วาง");
    };
  });
}

export function showImportPreview(r: ParseResult, sourceName: string) {
  if (!r.ok) {
    openSheet(head("นำเข้าไม่ได้")
      + `<p class="note"><span class="tag bad">ไฟล์ไม่ผ่านการตรวจ</span> ข้อมูล${ctx.mode === "cloud" ? "ในบัญชี" : "ในเครื่องนี้"}ยังอยู่ครบ ไม่มีอะไรถูกเปลี่ยน</p>`
      + `<div class="panel"><ul class="todo" id="im-errors">${r.errors.map(e => `<li>${esc(e)}</li>`).join("")}</ul></div>`
      + `<div class="stack" style="margin-top:14px"><button class="btn" id="im-retry">${ic("left")}เลือกไฟล์อื่น</button></div>`,
    sh => { sh.querySelector<HTMLElement>("#im-retry")!.onclick = () => openImportSheet(); });
    return;
  }
  const local = ctx.store.lib;
  const lt = totals(local);
  const localEmpty = !ctx.store.started || (lt.workspaces + lt.categories + lt.actions === 0);
  const plan = planImport(local, r.library);
  const addN = plan.toAdd.workspaces + plan.toAdd.categories + plan.toAdd.actions;
  let mode: "merge" | "replace" = "merge";
  let strategy: ConflictStrategy = "keep-local";

  const html = head("ตรวจข้อมูลก่อนนำเข้า")
    + `<div class="panel"><dl class="kv" id="im-summary">
        <dt>แหล่ง</dt><dd>${esc(sourceName)} · ${r.source === "prototype-v1" ? "ข้อมูลจากต้นแบบ" : "ไฟล์สำรอง MEMOLINK"}${r.exportedAt ? ` · ส่งออก ${dmy(r.exportedAt)}` : ""}</dd>
        <dt>ในไฟล์</dt><dd>${plan.inFile.workspaces} สถานที่ · ${plan.inFile.categories} หมวด · ${plan.inFile.actions} Action</dd>
        <dt>เพิ่มใหม่</dt><dd id="im-add">${plan.toAdd.workspaces} สถานที่ · ${plan.toAdd.categories} หมวด · ${plan.toAdd.actions} Action</dd>
        <dt>มีอยู่แล้ว</dt><dd id="im-same">${plan.identical} รายการ (เหมือนเดิม จะข้าม ไม่เพิ่มซ้ำ)</dd>
        <dt>ID ชนกัน</dt><dd id="im-conf">${plan.conflicts.length ? `<span class="tag">${plan.conflicts.length} รายการ</span> ID เดียวกันแต่ข้อมูลต่างกัน` : "ไม่มี"}</dd>
        ${localEmpty ? "" : `<dt>${here()}</dt><dd>${lt.workspaces} สถานที่ · ${lt.categories} หมวด · ${lt.actions} Action</dd>`}
      </dl></div>`
    + (plan.conflicts.length ? `<ul class="todo conf-list">${plan.conflicts.slice(0, 5).map(c => `<li>${esc(c.localName)}${c.localName !== c.incomingName ? ` → ในไฟล์ชื่อ “${esc(c.incomingName)}”` : " (เนื้อหาต่างกัน)"}</li>`).join("")}${plan.conflicts.length > 5 ? `<li>และอีก ${plan.conflicts.length - 5} รายการ</li>` : ""}</ul>` : "")
    + (r.warnings.length ? `<p class="note" style="margin-top:10px"><span class="tag">หมายเหตุ</span> ${esc(r.warnings[0])}${r.warnings.length > 1 ? ` และอีก ${r.warnings.length - 1} จุด` : ""}</p>` : "")
    + (r.source === "prototype-v1" ? `<p class="note" style="margin-top:10px">ข้อมูลจากต้นแบบรวมรายการตัวอย่างเดิมไว้ด้วย หลังนำเข้าลบรายการที่ไม่ต้องการได้ตามปกติ</p>` : "")
    + (plan.blocked.length ? `<p class="err" id="im-blocked" style="margin:12px 0">${plan.blocked.map(esc).join("<br>")}<br>นำเข้าไม่ได้ ข้อมูลในเครื่องไม่ถูกเปลี่ยน</p>` : "")
    + (localEmpty ? "" : `<div class="field" style="margin-top:14px"><span class="lab">วิธีนำเข้า</span><div class="seg" id="im-mode">
        <button type="button" data-m="merge" aria-pressed="true">รวมกับของเดิม</button>
        <button type="button" data-m="replace" aria-pressed="false">แทนที่ทั้งหมด</button></div></div>`)
    + (plan.conflicts.length && !localEmpty ? `<div class="field" id="im-strat-f"><span class="lab">รายการที่ ID ชนกัน</span><div class="panel">
        <label class="opt"><input type="radio" name="im-strat" value="keep-local" checked><span class="ot"><b>เก็บข้อมูล${ctx.mode === "cloud" ? "ในบัญชี" : "ในเครื่องนี้"}</b><small>ข้ามรายการจากไฟล์ที่ ID ชนกัน</small></span></label>
        <label class="opt"><input type="radio" name="im-strat" value="use-file"><span class="ot"><b>ใช้ข้อมูลจากไฟล์</b><small>เขียนทับ ${plan.conflicts.length} รายการด้วยข้อมูลจากไฟล์ (ID เดิม)</small></span></label></div></div>` : "")
    + (localEmpty ? "" : `<label class="opt confirm" id="im-replace-f" hidden><input type="checkbox" id="im-replace-ok"><span class="ot"><b>ยืนยันแทนที่</b><small>ข้อมูล${here()}ตอนนี้ ${lt.workspaces} สถานที่ · ${lt.categories} หมวด · ${lt.actions} Action จะถูกแทนที่ด้วยไฟล์นี้ (เก็บสำเนาไว้ย้อนกลับได้ 1 ครั้ง)</small></span></label>`)
    + `<p class="err" id="im-err" role="alert" style="margin:14px 0 0" hidden></p>`
    + `<div class="stack" style="margin-top:14px"><button class="btn primary" id="im-confirm">${ic("check")}<span></span></button><button class="btn ghost" data-do="close">ยกเลิก</button></div>`;

  openSheet(html, sh => {
    const btn = sh.querySelector<HTMLButtonElement>("#im-confirm")!;
    const replaceF = sh.querySelector<HTMLElement>("#im-replace-f");
    const replaceOk = sh.querySelector<HTMLInputElement>("#im-replace-ok");
    const stratF = sh.querySelector<HTMLElement>("#im-strat-f");
    const nothingNew = () => mode === "merge" && addN === 0 && (plan.conflicts.length === 0 || strategy === "keep-local");
    const sync = () => {
      if (replaceF) replaceF.hidden = mode !== "replace";
      if (stratF) stratF.hidden = mode !== "merge";
      const label = btn.querySelector("span")!;
      if (plan.blocked.length) { label.textContent = "นำเข้าไม่ได้"; btn.disabled = true; return; }
      if (mode === "replace") { label.textContent = "แทนที่ด้วยข้อมูลจากไฟล์"; btn.disabled = !replaceOk?.checked; return; }
      label.textContent = nothingNew() ? "ไม่มีข้อมูลใหม่ ปิด" : "ยืนยันนำเข้า";
      btn.disabled = false;
    };
    sh.querySelector("#im-mode")?.addEventListener("click", e => {
      const b = (e.target as Element).closest<HTMLElement>("[data-m]"); if (!b) return;
      mode = b.dataset.m as typeof mode;
      sh.querySelectorAll("#im-mode button").forEach(x => x.setAttribute("aria-pressed", String(x === b)));
      sync();
    });
    sh.querySelectorAll<HTMLInputElement>('input[name="im-strat"]').forEach(x => x.addEventListener("change", () => { strategy = x.value as ConflictStrategy; sync(); }));
    replaceOk?.addEventListener("change", sync);
    const errEl = sh.querySelector<HTMLElement>("#im-err")!;
    const fail = (msg: string) => { errEl.textContent = msg; errEl.hidden = false; };
    /** after "load latest" the summary is worked out again against the newest data */
    const recheck = () => showImportPreview(r, sourceName);
    let busy = false;
    btn.addEventListener("click", async () => {
      if (btn.disabled || busy) return;
      if (nothingNew()) { closeSheet(); toast("ข้อมูลครบอยู่แล้ว ไม่มีอะไรเพิ่ม"); return; }
      busy = true; errEl.hidden = true;
      try {
        const before = ctx.store.lib;
        const next = mode === "replace" || localEmpty ? applyReplace(plan) : applyMerge(before, plan, strategy);
        if (!localEmpty) {
          try { await ctx.store.repo.saveSafetyCopy(before, mode === "replace" ? "ก่อนแทนที่ด้วยไฟล์นำเข้า" : "ก่อนนำเข้าไฟล์"); }
          catch (e) {
            const reason = failReasonOf(e);
            if (reason === "storage") fail(SAFETY_FAILED_MESSAGE);
            else showFail(errEl, { ok: false, reason, message: FAIL_MESSAGES[reason] }, recheck);
            return;
          }
        }
        const res = await ctx.store.replace(next);
        await refreshSafety();
        if (!res.ok) { showFail(errEl, res, recheck); return; } // sheet stays open; the same file can be confirmed again
        closeSheet();
      ctx.V.view = "home"; ctx.V.catId = null; ctx.V.edit = false; ctx.render(); scrollTo(0, 0);
      const t = totals(next);
      toast(mode === "replace" || localEmpty ? `นำเข้าแล้ว ${t.workspaces} สถานที่ · ${t.categories} หมวด · ${t.actions} Action` : `นำเข้าแล้ว เพิ่ม ${addN} รายการ`);
      } finally { busy = false; }
    });
    sync();
  });
}

export async function undoLastImport() {
  const s = await ctx.store.repo.loadSafetyCopy(); if (!s) { toast("ไม่มีสำเนาให้ย้อนกลับ"); return; }
  const res = await ctx.store.replace(s.library);
  if (!res.ok) { ctx.render(); failNotice(res); return; }
  await ctx.store.repo.clearSafetyCopy();
  await refreshSafety();
  ctx.V.view = "home"; ctx.V.catId = null; ctx.render();
  toast(`ย้อนกลับเป็นข้อมูลเมื่อ ${dmyhm(s.savedAt)} แล้ว`);
}
